import { type ReactNode } from 'react';
import { Pin } from '../../../icons';
import './ListRow.css';

/**
 * ListRow — 메일 · 채팅 · 게시판 · 파일이 **공유할 한 행** (디자인 정본 E2).
 *
 * 지금은 `MailListRow` · `ChatList` 항목 · `BoardWideList` 행 · `FileListView` 행이 **각자**다.
 * 넷이 같은 일을 하는데 안 읽음 표시도, 선택 색도, 높이도 조금씩 다르다.
 *
 * ★★★ **안 읽음은 «굵기 + 점» 둘로 말한다.** 굵기만으로 가르면 목록을 훑을 때 안 보이고,
 *    점만 두면 무엇이 새것인지 글에서 안 드러난다. 색 하나로 가르지 않는 것과 같은 이유다.
 *
 * ★★ **메타(시각·건수)는 등폭 숫자**(`--font-num`)다. 행마다 자릿수가 달라 들쭉날쭉하면
 *    세로로 훑을 수 없다.
 *
 * ★ 두 줄이면 64, 한 줄이면 48. 줄 수가 곧 높이라 따로 받지 않는다.
 */
export interface ListRowProps {
  /** 왼쪽 — 아바타·아이콘·체크박스. */
  leading?: ReactNode;
  title: ReactNode;
  /** 제목 아래 한 줄. 주면 행이 64가 된다. */
  subtitle?: ReactNode;
  /** 제목 옆 표식 — 「공지」 같은 것. */
  badge?: ReactNode;
  /** 오른쪽 끝 — 날짜·건수. 등폭으로 그린다. */
  meta?: ReactNode;
  /** 오른쪽 — 손이 닿거나 **키보드 포커스가 들어올 때** 보인다. */
  actions?: ReactNode;
  unread?: boolean;
  selected?: boolean;
  pinned?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  className?: string;
}

export function ListRow({
  leading, title, subtitle, badge, meta, actions,
  unread = false, selected = false, pinned = false, disabled = false, onClick, className,
}: ListRowProps) {
  const cls = [
    'wc-lrow',
    subtitle != null ? 'wc-lrow--two' : '',
    unread ? 'is-unread' : '',
    selected ? 'is-selected' : '',
    disabled ? 'is-disabled' : '',
    onClick && !disabled ? 'is-clickable' : '',
    className ?? '',
  ].filter(Boolean).join(' ');

  return (
    <div
      className={cls}
      aria-selected={selected || undefined}
      aria-disabled={disabled || undefined}
      onClick={disabled ? undefined : onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick && !disabled ? 0 : undefined}
      onKeyDown={onClick && !disabled ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
    >
      {/* 안 읽음 점 — 굵기와 «함께» 말한다. 자리는 늘 잡아 둬서 글이 좌우로 안 흔들린다. */}
      <span className="wc-lrow__dot" aria-hidden />
      {leading != null && <span className="wc-lrow__lead">{leading}</span>}

      <span className="wc-lrow__body">
        <span className="wc-lrow__titleline">
          {pinned && <Pin className="wc-lrow__pin" aria-label="고정됨" />}
          <span className="wc-lrow__title">{title}</span>
          {badge != null && <span className="wc-lrow__badge">{badge}</span>}
        </span>
        {subtitle != null && <span className="wc-lrow__sub">{subtitle}</span>}
      </span>

      {meta != null && <span className="wc-lrow__meta">{meta}</span>}
      {actions != null && (
        <span className="wc-lrow__acts" onClick={(e) => e.stopPropagation()}>{actions}</span>
      )}
    </div>
  );
}
